import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import { ColorStylesGrid } from "@v1/components/designLibrary/ColorSavedStyleGroup";
import Popover from "@v1/components/designSystem/popover/Popover";
import useShopStyles from "@v1/hooks/designLibrary/useGetDesignLibrarySavedStyles";
import { useAreModalsOpen } from "@v2/store/modalStore";

type ColorPickerWrapperProps = {
  trigger: React.ReactNode;
  content: React.ReactNode;
  popoverTitle?: string;
  popoverSideOffset?: number;
  popoverSide?: "top" | "right" | "bottom" | "left";
  isPopoverOpen?: boolean;
  showSavedStyles?: boolean;
  allowsGradientSelection?: boolean;
  onOpenPopoverChange?(isOpen: boolean): void;
  onSelectSavedStyle?: (value: string) => void;
  onInteractOutside?: (event: CustomEvent) => void;
};

const ColorPickerWrapper: React.FC<ColorPickerWrapperProps> = ({
  content,
  trigger,
  popoverTitle,
  isPopoverOpen,
  popoverSideOffset,
  popoverSide,
  showSavedStyles,
  onOpenPopoverChange,
  onSelectSavedStyle,
  onInteractOutside,
}) => {
  const areModalsOpen = useAreModalsOpen();

  const { colorShopStyles, isLoading, designLibrary } = useShopStyles();

  const formattedOnSelectSavedStyle = (styleId: string) => {
    onSelectSavedStyle?.(
      `{{ library.${designLibrary?.id}.styles.${styleId}.attributes.color }}`,
    );
  };

  return (
    <Popover
      title={popoverTitle}
      isOpen={isPopoverOpen}
      onOpenChange={onOpenPopoverChange}
      shouldPreventDefaultOnInteractOutside={areModalsOpen}
      sideOffset={popoverSideOffset}
      side={popoverSide}
      onInteractOutside={onInteractOutside}
      onRequestClose={() => {
        onOpenPopoverChange?.(false);
      }}
      triggerAsChild
      content={
        <div className="flex w-full flex-col gap-2">
          {content}
          {showSavedStyles && (
            <>
              <Separator />
              <div className="text-xs">Color Styles</div>
              <ColorStylesGrid
                colorSavedStyles={colorShopStyles}
                isLoading={isLoading}
                onColorClick={formattedOnSelectSavedStyle}
                modificationEnabled={false}
                showTooltips={true}
              />
            </>
          )}
        </div>
      }
    >
      <div
        data-testid={
          popoverTitle
            ? `${popoverTitle.toLowerCase()}-gradient-selector`
            : undefined
        }
      >
        {trigger}
      </div>
    </Popover>
  );
};

export default ColorPickerWrapper;
